import { Add } from '@mui/icons-material'
import { ButtonBase, Tooltip } from '@mui/material'
import { ChangeEvent } from 'react'
import { useMatch } from 'react-router-dom'
import { BucketUserRole } from '../../../../graphql/generated'
import useBucket from '../../hooks/useBucket'
import useBucketConvertProgress from '../../hooks/useBucketConvertProgress'
import useBucketMediaUpload from '../../hooks/useBucketMediaUpload'
import useBucketMigrationProgress from '../../hooks/useBucketMigrationProgress'
import useBucketPlan from '../../hooks/useBucketPlan'

interface Props {
  selectModeEnabled: boolean
}

// Aligns with constants kept in datasources
export const SUPPORTED_VIDEO_TYPES = [
  '.3gp2',
  '.asf',
  '.mov',
  '.vob',
  '.mp4',
  '.flv',
  '.webm',
  '.mpg',
  '.mpeg',
  '.dvd',
  '.mkv',
  '.swf',
  '.3gp',
  '.avi',
  '.quicktime'
]
export const SUPPORTED_IMAGE_TYPES = ['.webp', '.jpg', '.png', '.jpeg', '.bmp', '.gif', '.heic', '.tiff', '.tif', '.heif']

export default function UploadIconButton({ selectModeEnabled }: Readonly<Props>) {
  const { restrictions, tooltipTitle } = useBucketPlan()
  const { bucketUser } = useBucket()
  const { uploadMultiple } = useBucketMediaUpload()
  const isMigrationInProgressForBucket = useBucketMigrationProgress()
  const isBucketConvertInProgressForBucket = useBucketConvertProgress()

  const disabled =
    (bucketUser?.userRole === BucketUserRole.Viewer && !bucketUser?.permissions?.mediaCreate) ||
    isMigrationInProgressForBucket ||
    isBucketConvertInProgressForBucket
  const restricted = restrictions.uploadMedia || disabled
  const path = useMatch('/bucket/:bucketId/:slug')
  const isActivityFeed = path?.params.slug === 'activity'
  const hideTooltip = !restricted
  const title = tooltipTitle('uploadMedia')
  const userCannotCreateMediaTitle =
    isMigrationInProgressForBucket || isBucketConvertInProgressForBucket
      ? 'Media upload is disabled during the migration process'
      : 'You do not have permission to upload media'

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    uploadMultiple(event.target.files)
  }

  const onInputClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (disabled) {
      event.preventDefault()
    }
    const element = event.target as HTMLInputElement
    element.value = ''
  }

  const acceptList = `${[...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_VIDEO_TYPES].join(', ')}`

  return (
    <label htmlFor="contained-button-file" data-test="label-media-upload">
      <input
        accept={acceptList}
        style={{ opacity: 0, position: 'absolute', width: '20px' }}
        id="contained-button-file"
        type="file"
        multiple
        onChange={handleChange}
        onClick={onInputClick}
        data-testid="file-upload-nav-pill"
      />
      <Tooltip
        PopperProps={{
          disablePortal: hideTooltip
        }}
        disableFocusListener={hideTooltip}
        disableHoverListener={hideTooltip}
        disableTouchListener={hideTooltip}
        title={disabled ? userCannotCreateMediaTitle : title}
      >
        <span>
          <ButtonBase
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'none',
              border: '4px solid white',
              width: '70px',
              height: '70px',
              borderRadius: '60px',
              background: 'linear-gradient(154deg, #1C47CB 13.64%, #02E39F 102.07%)',
              '&:hover': {
                cursor: 'pointer',
                background: 'linear-gradient(154deg, #02E39F 13.64%, #1C47CB 102.07%)'
              },
              '&.Mui-disabled': {
                opacity: '0.3'
              }
            }}
            component="span"
            data-pendo="navpill-upload"
            disabled={restricted || isActivityFeed || selectModeEnabled}
            aria-label="Upload Media"
            id="appbar-upload"
          >
            <Add sx={{ color: 'white', fontSize: '3rem' }} />
          </ButtonBase>
        </span>
      </Tooltip>
    </label>
  )
}
